<template>
  <div>
    <div :id="chartId" :style="{width: width, height: height}" :auto-resize="autoResize"/>
  </div>
</template>
  
<script>
import resize from '@/views/mixins/resize'

const animationDurationValue = 800
const animationEasingValue = 'cubicInOut'
const axisTextColor = '#888888'
const lengendTextColor = '#000000'
const barTextColor = '#000000'

export default {
  mixins: [resize],
  props: {
    autoResize: {
      type: Boolean,
      default: true
    },
    plantId: {
      type: Number,
      required: false,
      default: ''
    },
    chartData: {
      type: Array,
      required: true,
      default: []
    },
    chartId: {
      type: String,
      required: false,
      default: ''
    },
    ratioTitle: {
      type: String,
      required: false,
      default: ''
    },
    lineTitle: {
      type: String,
      required: false,
      default: ''
    },
    width: {
      type: String,
      required: false,
      default: '100%'
    },
    height: {
      type: String,
      required: false,
      default: '400px'
    }
  },
  data() {
    return {
      successColor: '',
      dangerColor: '',
      warningColor: '',
      mainChart: null,
      refreshTimer: null,

      yearLabelList: [],
      monthLabelList: [],
      groupXAxisList: [],

      dateLabelList: [],
      ratioDataList: [],
      lineDataList: [],

      seriesOption: []
    };
  },
  watch: {
    chartData: {
      deep: true,
      handler(val) {
        this.setOptionByElement(val)
      }
    },
    lineTitle: {
      handler() {
        if (this.mainChart != null) {
          this.setOptionByElement(this.chartData);
        }
      }
    }
  },
  mounted() {
    this.initChart();
    //监听窗口resize事件
    window.addEventListener('resize', this.onResizeHandler);
  },
  beforeDestroy() {
    if (this.mainChart === null) {
      return
    } else {
      if(this.refreshTimer != null) {
        clearTimeout(this.refreshTimer);
        this.refreshTimer = null;
      }
      this.mainChart.dispose();
      this.mainChart = null;
      //销毁窗口resize事件
      window.removeEventListener('resize', this.onResizeHandler);
    }
  },
  methods: {
    onResizeHandler() {
      if (this.mainChart != null) {
        this.mainChart.resize();
      }
    },
    initChart() {
      var rootVar = document.querySelector(':root');
      this.successColor = getComputedStyle(rootVar).getPropertyValue('--chartSuccessColor');
      this.dangerColor = getComputedStyle(rootVar).getPropertyValue('--chartDangerColor');
      this.warningColor = getComputedStyle(rootVar).getPropertyValue('--chartWarningColor');

      var chartDom = document.getElementById(this.chartId);
      this.mainChart = this.echarts.init(chartDom);
      this.setOptionByElement(this.chartData);
    },
    drawLoadingText(loadingText) {
      if (this.mainChart != null) {
        this.mainChart.hideLoading();
        this.mainChart.showLoading({
          text: loadingText,
          color: 'transparent',
          fontSize: 18,
          textColor: '#8a8e91',
          maskColor: 'transparent'
        }); 
        this.mainChart.clear();
        this.mainChart.setOption({});
      }
    },
    splitYearMonth(element) {
      this.yearLabelList = [];
      this.monthLabelList = [];
      this.groupXAxisList = [];

      this.dateLabelList = [];
      this.ratioDataList = [];
      this.lineDataList = [];

      // 遍历源数据，生成所需的图表数据、分组轴标签、分组轴刻度数据
      element.forEach((dataItem) => {

        var dateArray = this.parseDateArray(dataItem.month);
        if(dateArray.length === 2) {
          if(this.yearLabelList.find(item => (item === dateArray[0])) === undefined) {
            this.yearLabelList.push(dateArray[0]);
            this.groupXAxisList.push({ 
              name: dateArray[0],
              showAxisTick: true
            });
          } else {
            this.yearLabelList.push(dateArray[0]);
            this.groupXAxisList.push({ 
              name: dateArray[0],
              showAxisTick: false
            });
          }

          this.monthLabelList.push(dateArray[1]);

          this.dateLabelList.push(dataItem.month);
          this.ratioDataList.push(dataItem.ratio);
          this.lineDataList.push(dataItem.target);
        }
      });

      if(this.isEmpty(this.groupXAxisList) === false) {
        var tempStats = [];
        var tempDataName = '';
        var tempDataSum = 0;
        this.groupXAxisList.forEach((dataItem, dataIndex) => {
          if(tempDataName != dataItem.name) {
            if(dataIndex === 0) {
              //第一个, 不保存之前的统计数据
              tempDataName = dataItem.name;
              tempDataSum = 1;
            } else {
              //同名称的最后一个,先保存之前的统计数据
              tempStats.push({
                name: tempDataName,
                sum: tempDataSum
              })

              //重新开始一个统计
              tempDataName = dataItem.name;
              tempDataSum = 1;
            }
          } else {
            //同名称的数据,每次+1
            tempDataSum += 1;
          }

          //最后一个数据,强制保存
          if((dataIndex + 1) === this.groupXAxisList.length) {
            tempStats.push({
              name: tempDataName,
              sum: tempDataSum
            })
          }
        });

        tempStats.forEach((statsItem) => {
          var showAxisLabelIndex = Math.ceil(statsItem.sum / 2);
          var tempCount = 0;
          this.groupXAxisList.forEach((dataItem, dataIndex) => {

            if(statsItem.name === dataItem.name) {
              if((tempCount + 1) === showAxisLabelIndex) {
                dataItem.showAxisLabel = true;
                tempCount += 1;
              } else {
                tempCount += 1;
                dataItem.showAxisLabel = false;
              }
            }
          });
        });
      }
    },
    setOptionByElement(element) {
      if (this.mainChart != null) {

        if(this.isEmpty(element) === true) {
          this.drawLoadingText(this.$parseLabel('加载中'));

          if(this.refreshTimer != null) {
            clearTimeout(this.refreshTimer);
            this.refreshTimer = null;
          }
          
          this.refreshTimer = setTimeout(() => {
            if(this.isEmpty(element) === true) {
              this.drawLoadingText(this.$parseLabel('暂无数据'));
            }
          }, 5000);
        } else {
          //拆分双层X轴数据
          this.splitYearMonth(element);
          
          if(this.refreshTimer != null) {
            clearTimeout(this.refreshTimer);
            this.refreshTimer = null;
          }
          this.mainChart.hideLoading();
          this.mainChart.clear();

          this.seriesOption = [];
          //实际数据
          this.seriesOption.push({
            name: this.ratioTitle, 
            type: 'line', //类型     
            showBackground: false, 
            showSymbol: true,     
            symbolSize: 8,
            smooth: true,
            data: this.ratioDataList,
            label: {
              show: false,
              fontWeight: 'bold',
              position: 'top',
              color: barTextColor
            },
            itemStyle: {
              borderRadius: [2, 2, 0, 0],
              color: ((params) => {
                if(this.isEmpty(this.lineDataList) === false) {
                  let lineDataValue = this.lineDataList[params.dataIndex];
                  if(lineDataValue != null) {
                    if(params.value >= lineDataValue) {
                      return this.successColor;
                    } else {
                      return this.dangerColor;
                    }
                  } else {
                    return this.successColor;
                  }
                } else {
                  return this.successColor;
                }
              })
            },
            emphasis: {
              itemStyle: {
                focus: 'series'
              }
            }   
          });

          //和而泰工厂选择全球时,不显示target
          if(this.plantId != 0) {
            this.seriesOption.push({
              name: this.lineTitle,
              type: 'line',  
              showSymbol: false,
              smooth: true,
              data: this.lineDataList,
              lineStyle: {
                type: 'dashed'
              },
              label: {
                show: false,
                fontWeight: 'bold',
                position: 'bottom',
                formatter: function(params) {
                  return params.value + '%';
                }
              },
              itemStyle: {
                color: this.warningColor
              }
            });
          }

          this.mainChart.setOption({  
            color: [this.successColor, this.warningColor],
            tooltip: {
              trigger: "axis",
              axisPointer: {
                type: 'shadow'
              },
              formatter: (params) => {
                var title = '';
                var content = '';

                if((params != null) && (params.length > 0)) {
                  params.forEach((paramItem, index)=>{
                    if(index === 0) {
                      var realDate = this.dateLabelList[paramItem.dataIndex];
                      if(this.isEmpty(realDate) === false) {
                        title = this.parseDateTooltips(realDate) + '<br/>'; 
                      } else {
                        title = '标题<br/>'; 
                      }
                    }

                    if(paramItem.value != null) {
                      if(content.length > 0) {
                        content += '<br/>';
                      }
                      content +='<span style="display:inline-block;margin-right:5px;border-radius:50%;width:10px;height:10px;left:5px;background-color:'+paramItem.color+'"></span>';
                      content +='<span style="display:inline-block;width:auto;height:10px;">' + paramItem.seriesName + '：</span>';
                      content +='<span style="display:inline-block;width:auto;height:10px;right:5px;">' + paramItem.value + '%</span>'; 
                    }
                  });
                }

                return title + content;
              }
            },
            legend: {
              show: true,
              x: "center",  
              textStyle: {//图例文字的样式
                color: lengendTextColor,
                fontSize: 16
              }
            }, 
            grid: {
              show: false,
              left: "75",
              top: "12%",
              right: "3%",
              bottom: "60"
            },
            xAxis: [{
              position: 'bottom',
              type: 'category',
              data: this.monthLabelList,
              axisLine: {
                show: true,
                lineStyle: {
                  color: axisTextColor
                },            
              },
              axisLabel: {
                textStyle: {
                  fontSize: 12
                }
              },
              axisTick: {
                show: true,
                length: 25, //刻度线长度
                //interval: 0
              }                    
            },{
              position: 'bottom',
              type: 'category',
              data: this.yearLabelList,
              axisLine: {
                show: true,
                lineStyle: {
                  color: axisTextColor
                }              
              },
              axisLabel: {
                textStyle: {
                  fontSize: 12
                },
                margin: 30, //标签到轴线的距离,
                interval: (index, value) => {
                  if(this.groupXAxisList[index].showAxisLabel === true) {
                    return value;
                  } else {
                    return false;
                  }
                }
              },
              axisTick: {
                length: 45, //刻度线长度
                interval: (index, value) => {
                  if(this.groupXAxisList[index].showAxisTick === true) {
                    return value;
                  } else {
                    return false;
                  }
                }
              }                 
            }],
            yAxis: [{
              type: 'value',
              axisLine: {
                show: true,
                lineStyle: {
                  color: axisTextColor
                }             
              },
              axisLabel: {
                textStyle: {
                  fontSize: 14
                },
                formatter: function(value) {
                  if(value === 0) {
                    return '';
                  }
                  else {
                    return value + '%';
                  }
                }
              },
              min: function(value) {
                return (value.min >= 0.1) ? (value.min - 0.1).toFixed(1) : 0;
              },
              max: function(value) {
                return (value.max <= 99.9) ? (value.max + 0.1).toFixed(1) : 100;
              }
            }],
            series: this.seriesOption,
            animationDuration: animationDurationValue,
            animationEasing: animationEasingValue
          });
        }
      }
    }
  },
};
</script>
  
<style lang="less" scoped>
</style>